import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Repeat, Pencil } from 'lucide-react'
import { Timestamp } from '../ui/Timestamp'
import { InlinePopover, ChipRow } from '../tasks/inline/InlinePopover'

/**
 * One occurrence model, two policies (owner 2026-09-05): a repeating task and
 * a habit share the same record — a date, an outcome, a note — and differ in
 * what the summary says. This section reads the server's read model for
 * either kind and shows the last few weeks as a strip plus the recent ones
 * with their notes. Tone: what happened, never a scolding.
 */
export type Outcome = 'done' | 'skipped' | 'missed' | 'pending'

export interface Occurrence {
  key: string
  date: string
  at?: string | null
  outcome: Outcome
  progress?: number | null
  note?: string | null
  future?: boolean
  /** Habit step ids ticked on this occurrence (per-occurrence ticking, 2026-09-06). */
  steps_done?: string[]
  /** A skill's practice log (Skills UX pass 2026-09-06): a finished task or a habit done under it — read-only here. */
  kind?: 'task' | 'habit'
  task_id?: number | null
  habit_id?: number | null
}

export interface StepDef { id: string; text: string }

export interface OccurrenceSummary {
  done: number
  missed: number
  skipped: number
  pending: number
  rate?: number | null
  streak?: number | null
  next_date?: string | null
  ends?: string | null
  words?: string | null
}

export interface OccurrencesPayload {
  policy: 'task' | 'habit' | 'skill' | 'goal' | 'dream' | 'once'
  window?: { from: string; to: string }
  summary?: OccurrenceSummary | null
  occurrences: Occurrence[]
}

export type OccurrenceItemType = 'task' | 'habit' | 'skill' | 'goal' | 'dream'

interface Props {
  itemType: OccurrenceItemType
  itemId: number
  refreshKey?: string | number | null
  headingClass?: string
  /** The habit's steps, so each time can say which were done and tick them. */
  steps?: StepDef[]
  /** Called after a write, so the page can refresh what it derives from today. */
  onWrote?: () => void
  /** Only the log: no words line, no strip, no "Next …" footer. The habit page
   *  passes this because its rail already carries the run (Keeping up), the
   *  strip and the next time (Plan) — on 2026-09-12 that page said "next" in
   *  four places and the run in three (North Star rule 2). */
  logOnly?: boolean
}

const DOT: Record<Outcome, string> = {
  done: 'bg-ok',
  skipped: 'bg-soft',
  missed: 'bg-warn/60',
  pending: 'bg-transparent border border-soft',
}
const WORD: Record<Outcome, string> = { done: 'done', skipped: 'skipped', missed: 'not this time', pending: 'coming up' }
const CHOICES = [
  { value: 'done', label: 'Done' },
  { value: 'skipped', label: 'Not this time' },
  { value: 'pending', label: 'Take it back' },
]

/** Where a write for this occurrence goes; the key carries the plane's own id.
 *  A skill's log is read-only — its rows are written on the task and habit pages. */
function writeUrl(itemType: OccurrenceItemType, itemId: number, o: Occurrence): string | null {
  const parts = o.key.split(':')
  if (itemType === 'task') return `/api/hierarchy/tasks/${itemId}/occurrences/${o.date}`
  if (itemType === 'skill' || itemType === 'goal' || itemType === 'dream') return null
  const occId = parts[2]
  return occId && occId !== 'undefined' ? `/api/hierarchy/habits/${itemId}/occurrences/${occId}` : null
}

function OccurrenceEditor({ itemType, itemId, occurrence, onSaved, compact = false, steps = [] }: { itemType: OccurrenceItemType; itemId: number; occurrence: Occurrence; onSaved: (p: OccurrencesPayload) => void; compact?: boolean; steps?: StepDef[] }) {
  const [outcome, setOutcome] = useState<string>('')
  const [note, setNote] = useState('')
  const [ticked, setTicked] = useState<string[]>([])
  const url = writeUrl(itemType, itemId, occurrence)
  if (!url) return null
  const canTick = itemType === 'habit' && steps.length > 0 && !occurrence.future
  // "Take it back" only makes sense once something was recorded; a note is
  // always allowed, whatever the outcome (owner 2026-09-06).
  const choices = CHOICES.filter((c) => c.value !== 'pending' || occurrence.outcome === 'done' || occurrence.outcome === 'skipped')
  const reset = () => { setOutcome(''); setNote(''); setTicked(occurrence.steps_done || []) }
  const ticksChanged = canTick && JSON.stringify([...ticked].sort()) !== JSON.stringify([...(occurrence.steps_done || [])].sort())
  const save = async () => {
    if (!outcome && !note.trim() && !ticksChanged) throw new Error('Pick what happened, tick a step, or leave a note.')
    const r = await fetch(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome: outcome || undefined, note: note.trim() || undefined, steps_done: ticksChanged ? ticked : undefined }) })
    const body = await r.json().catch(() => ({}))
    if (!r.ok || body.success === false) throw new Error(body.error || 'Could not save that')
    onSaved(body.data as OccurrencesPayload)
  }
  return (
    <InlinePopover label={`This time · ${occurrence.date}`} variant={compact ? 'icon' : 'chip'} onOpen={reset} onSave={save} testId="occurrence-edit" width="w-72"
                   trigger={compact ? <Pencil className="h-3 w-3" /> : <>Done today</>}>
      <p className="text-app-micro text-muted">Now: {WORD[occurrence.outcome]}. Change it, or just leave a note.</p>
      <ChipRow label="What happened" options={choices} value={outcome} onChange={setOutcome} />
      {canTick && (
        <ul className="space-y-1" aria-label="Steps this time" data-testid="occurrence-steps">
          {steps.map((s) => {
            const on = ticked.includes(s.id)
            return (
              <li key={s.id}>
                <label className="flex items-start gap-2 text-sm text-primary">
                  <input type="checkbox" checked={on} onChange={() => setTicked((cur) => (on ? cur.filter((x) => x !== s.id) : [...cur, s.id]))} className="mt-1 h-4 w-4 accent-brand" />
                  <span className={on ? 'text-muted line-through decoration-soft' : ''}>{s.text}</span>
                </label>
              </li>
            )
          })}
        </ul>
      )}
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="A word about it (optional)" aria-label="Note" className="w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30" />
    </InlinePopover>
  )
}

export function OccurrencesSection({ itemType, itemId, refreshKey, headingClass = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary', steps = [], onWrote, logOnly = false }: Props) {
  const [data, setData] = useState<OccurrencesPayload | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [all, setAll] = useState(false)

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/hierarchy/${itemType}s/${itemId}/occurrences`, { credentials: 'include' })
      const body = await r.json().catch(() => ({}))
      if (!r.ok || body.success === false) throw new Error(body.error || 'Could not load the occurrences')
      setData(body.data as OccurrencesPayload)
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load the occurrences')
      setData(null)
    }
  }, [itemType, itemId])
  useEffect(() => { void load() }, [load, refreshKey])

  if (err) return <p className="text-xs text-bad" role="alert">{err}</p>
  if (!data) return <p className="text-sm text-muted">Loading…</p>
  if (data.policy === 'once') return null
  if (data.occurrences.length === 0) {
    if (data.policy !== 'skill' && data.policy !== 'goal' && data.policy !== 'dream') return null
    return (
      <section data-testid="occurrences">
        <h3 className={headingClass}><Repeat className="h-3.5 w-3.5 text-brand" /> {data.policy === 'goal' || data.policy === 'dream' ? 'What got done' : 'Practice log'}</h3>
        <p className="mt-2 text-app-meta text-muted" data-testid="occurrences-empty">{data.policy === 'dream' ? 'Nothing yet. Something done under one of its goals shows up here.' : data.policy === 'goal' ? 'Nothing yet. A task finished or a habit done under this goal shows up here.' : 'Nothing yet. A step finished or a habit done under this skill shows up here.'}</p>
      </section>
    )
  }

  const s = data.summary
  const todayKey = new Date().toISOString().slice(0, 10)
  const todays = data.occurrences.find((o) => o.date === todayKey && o.outcome === 'pending')
  const past = data.occurrences.filter((o) => !o.future)
  const upcoming = data.occurrences.filter((o) => o.future)
  const strip = past.slice(-42)
  const recent = [...past].reverse()
  const shown = all ? recent : recent.slice(0, 5)

  return (
    <section data-testid="occurrences">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className={headingClass}><Repeat className="h-3.5 w-3.5 text-brand" /> {data.policy === 'goal' || data.policy === 'dream' ? 'What got done' : data.policy === 'skill' ? 'Practice log' : data.policy === 'habit' ? 'Each time' : 'Each time it comes round'}</h3>
        <div className="flex items-center gap-3">
          {!logOnly && s?.words && <p className="text-app-meta text-secondary" data-testid="occurrences-words">{s.words}</p>}
          {todays && <OccurrenceEditor itemType={itemType} itemId={itemId} occurrence={todays} onSaved={(p) => { setData(p); onWrote?.() }} steps={steps} />}
        </div>
      </div>

      {!logOnly && <div className="mt-2 flex flex-wrap items-center gap-1" aria-label="Recent occurrences" data-testid="occurrences-strip">
        {strip.map((o) => (
          <span key={o.key} title={`${o.date} · ${WORD[o.outcome]}${o.note ? ` · ${o.note}` : ''}`} className={`h-2.5 w-2.5 rounded-full ${DOT[o.outcome]}`} />
        ))}
        {upcoming.slice(0, 3).map((o) => (
          <span key={o.key} title={`${o.date} · coming up`} className="h-2.5 w-2.5 rounded-full border border-dashed border-strong" />
        ))}
      </div>}

      <ol className="mt-2 space-y-1 text-sm" data-testid="occurrences-list">
        {shown.map((o) => (
          <li key={o.key} className="flex items-start gap-2">
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[o.outcome]}`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <span className="text-primary">{(data.policy === 'skill' || data.policy === 'goal' || data.policy === 'dream') ? (o.kind === 'habit' ? 'Habit done' : data.policy === 'skill' ? 'Finished a step' : 'Finished a task') : WORD[o.outcome].charAt(0).toUpperCase() + WORD[o.outcome].slice(1)}</span>
              {(data.policy === 'skill' || data.policy === 'goal' || data.policy === 'dream') && o.kind === 'task' && o.task_id && <Link to={`/tasks/${o.task_id}`} className="ml-2 text-app-micro text-brand hover:underline">open</Link>}
              {(data.policy === 'skill' || data.policy === 'goal' || data.policy === 'dream') && o.kind === 'habit' && o.habit_id && <Link to={`/habits/${o.habit_id}`} className="ml-2 text-app-micro text-brand hover:underline">open</Link>}
              <OccurrenceEditor itemType={itemType} itemId={itemId} occurrence={o} onSaved={(p) => { setData(p); onWrote?.() }} compact steps={steps} />
              {steps.length > 0 && (o.steps_done?.length || 0) > 0 && <span className="text-secondary"> · {o.steps_done!.length} of {steps.length} steps</span>}
              {typeof o.progress === 'number' && o.progress > 0 && o.progress < 100 && <span className="text-secondary"> · {Math.round(o.progress)}%</span>}
              {o.note && <span className="text-secondary" title={o.note}> · {o.note.length > 90 ? `${o.note.slice(0, 89)}…` : o.note}</span>}
              <div className="text-app-micro text-muted">{o.at ? <Timestamp value={o.at} mode="datetime" /> : <Timestamp value={`${o.date}T12:00:00`} mode="date" />}</div>
            </div>
          </li>
        ))}
      </ol>
      {recent.length > 5 && (
        <button type="button" onClick={() => setAll((a) => !a)} className="mt-1 text-app-meta font-medium text-brand hover:underline">
          {all ? 'Show fewer' : `Show all ${recent.length}`}
        </button>
      )}
      {!logOnly && upcoming.length > 0 && (
        <p className="mt-2 text-app-meta text-muted" data-testid="occurrences-next">
          Next {upcoming[0].at ? <Timestamp value={upcoming[0].at} mode="datetime" /> : <Timestamp value={`${upcoming[0].date}T12:00:00`} mode="date" />}
          {upcoming.length > 1 ? `, then ${upcoming.length - 1} more in the next two weeks` : ''}
        </p>
      )}
    </section>
  )
}

export default OccurrencesSection
